// The consult event timeline (contract §9 pins path, default export, `{session}` prop) —
// "the dispute answer" (gateway-flow-c-design.md §10): one row per event in `seq` order,
// bridged gaps visibly marked, the grace period stated when non-zero. Reused as the
// superadmin drill-down (same design doc line) inside GatewayConsole's Consults tab, and
// embedded in ConsultCompleteDialog so the practitioner sees what they are about to close
// out — same read-only rendering everywhere, since it is evidence, not a form.
import React from "react";
import { useT } from "@/lib/LanguageContext";
import {
  LogIn, LogOut, Square, AlertTriangle, Video, DoorOpen, Clock3, XCircle, HelpCircle,
} from "lucide-react";
// Relative: `gateway/contract.js` has no existing "@/" Jest shim (see
// gateway/__testutils__/mocks.js) — it must resolve for real.
import { EVIDENCE_GRADES } from "../../gateway/contract";

const EVENT_ICONS = {
  room_opened: DoorOpen,
  participant_joined: LogIn,
  participant_left: LogOut,
  practitioner_ended: Square,
  meeting_started: Video,
  meeting_ended: Video,
  gap_detected: AlertTriangle,
  auto_completed: Clock3,
  rejected_event: XCircle,
};

const EVIDENCE_KEYS = {
  server_events: "consult.timeline.evidence.server_events",
  join_and_heartbeat: "consult.timeline.evidence.join_and_heartbeat",
  declared: "consult.timeline.evidence.declared",
};

const fmtTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return String(iso); }
};

const fmtDuration = (totalSeconds) => {
  const s = Math.max(0, Math.round(totalSeconds || 0));
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return `${m}m ${rem}s`;
};

/** @param {import("../../gateway/contract").ConsultSession} [session] */
export default function SessionTimeline({ session }) {
  const t = useT("admin");

  if (!session) {
    return (
      <div className="text-sm text-eh-muted text-center py-6" data-testid="session-timeline-empty">
        {t("consult.timeline.noSession")}
      </div>
    );
  }

  const events = [...(session.events || [])].sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0));
  // THE WIRE SHAPE, not the stored document's. `GET /appointments/{id}/session`
  // (consults.timeline_view) answers camelCase, and puts the three duration figures under
  // `computation` — they are the computation, not properties of the session. This
  // component read `session.evidence_quality` / `.raw_seconds` / `.billable_minutes`
  // straight off the top level, which no response has ever carried: the evidence chip
  // rendered "unknown" and all three figures were silently absent on every real consult.
  const computation = session.computation || {};
  const evidence = session.evidenceQuality || computation.evidenceQuality;
  const evidenceKnown = EVIDENCE_GRADES.includes(evidence);
  const { rawSeconds, billableMinutes, gracePeriodSeconds } = computation;
  // ZERO IS NOT A MEASUREMENT UNTIL THE SERVER HAS MADE ONE. `computation_response`
  // coerces every absent figure with `int(... or 0)`, so an open session answers
  // `rawSeconds: 0, billableMinutes: 0` — and a `!= null` gate renders them. The
  // practitioner's Complete dialog therefore read "Raw duration: 0m 0s · Billable
  // minutes: 0" immediately before confirming a consult that billed a real amount, which
  // reads as "nothing was recorded, this is a no-show" — the one conclusion that changes
  // what they click.
  //
  // Keyed on `computedBy` — the one field that means "a computation was MADE, by this
  // actor". `consult_repo.claim_completion` writes `computed_by` in the same conditional
  // write that moves the session to `computing`, so it is absent for exactly as long as
  // the figures are placeholders and present the moment they are real. Status is the
  // wrong signal: it is a lifecycle position, its vocabulary has drifted from what
  // `timeline_view` emits, and a new terminal state would silently start hiding figures.
  //
  // Once a computation exists a genuine zero is a FINDING — nobody was ever both-present —
  // and must be stated, because it is the reason a hold is about to be released. Same
  // three-state discipline the gateway console applies to a reconciliation that never ran.
  const isComputed = Boolean(computation.computedBy || session.computedBy);

  return (
    <div className="space-y-4" data-testid="session-timeline">
      <div className="flex flex-wrap items-center gap-3 text-xs text-eh-muted">
        <span className="inline-flex items-center gap-1.5 bg-eh-cream rounded-full px-2.5 py-1" data-testid="session-timeline-evidence">
          <HelpCircle className="w-3 h-3" />
          {t(evidenceKnown ? EVIDENCE_KEYS[evidence] : "consult.timeline.evidence.unknown")}
        </span>
        {!isComputed && (
          <span className="inline-flex items-center gap-1.5" data-testid="session-timeline-not-computed">
            {t("consult.timeline.notComputedYet")}
          </span>
        )}
        {isComputed && rawSeconds != null && (
          <span data-testid="session-timeline-raw">
            {t("consult.timeline.rawDuration", { duration: fmtDuration(rawSeconds) })}
          </span>
        )}
        {isComputed && billableMinutes != null && (
          <span data-testid="session-timeline-billable">
            {t("consult.timeline.billableMinutes", { minutes: billableMinutes })}
          </span>
        )}
        {/* "stated when non-zero" (contract §9) — a zero grace window is not a fact worth
            a line, it is the default. */}
        {!!gracePeriodSeconds && (
          <span className="text-amber-700 font-medium" data-testid="session-timeline-grace">
            {t("consult.timeline.gracePeriod", { seconds: gracePeriodSeconds })}
          </span>
        )}
      </div>

      {events.length === 0 ? (
        <p className="text-sm text-eh-muted" data-testid="session-timeline-no-events">{t("consult.timeline.noEvents")}</p>
      ) : (
        <ol className="space-y-2">
          {events.map((ev, i) => {
            const Icon = EVENT_ICONS[ev.type] || HelpCircle;
            const isGap = ev.type === "gap_detected";
            const isRejected = ev.type === "rejected_event";
            return (
              <li
                key={`${ev.seq ?? i}-${ev.type}-${ev.at}`}
                data-testid={`session-timeline-event-${i}`}
                data-event-type={ev.type}
                className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm ${
                  isGap ? "bg-amber-50 border border-amber-200"
                  : isRejected ? "bg-slate-50 border border-slate-200 text-eh-muted line-through"
                  : "bg-eh-cream/60"
                }`}
              >
                <Icon className={`w-4 h-4 flex-shrink-0 ${isGap ? "text-amber-600" : isRejected ? "text-slate-400" : "text-eh-sage-deep"}`} />
                <span className="font-medium">
                  {t(`consult.timeline.event.${ev.type}`, null, ev.type)}
                  {ev.role ? ` — ${t(`consult.timeline.role.${ev.role}`, null, ev.role)}` : ""}
                </span>
                {isGap && (
                  <span className="text-xs text-amber-700 font-semibold" data-testid={`session-timeline-gap-badge-${i}`}>
                    {t("consult.timeline.bridgedGap")}
                  </span>
                )}
                <span className="ml-auto text-xs text-eh-muted whitespace-nowrap">{fmtTime(ev.at)}</span>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
